import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../config';
import { getPlan, getStand } from '../services/rondte_services';
import { getNotasByRondteId, getPunteByRondteId } from '../services/merk_services';
import { getMerkbladsByRondteId } from '../services/merkblad_services';
import { fetchAllTeams } from '../services/span_services';
import SpanVergelyking from '../beoordelaaradmin/span_vergelyking';
import SpanScoresBreakdown from '../beoordelaaradmin/span_scores_breakdown';

const cardStyle = {
  backgroundColor: 'white',
  padding: '0.9rem 1rem',
  borderRadius: '8px',
  boxShadow: '0 1px 4px rgba(14, 30, 59, 0.12)',
  marginBottom: '0.9rem'
};

const STATUS = {
  beplan: { label: 'Beplan', background: '#eceff4', color: '#4b5563' },
  oop: { label: 'Oop', background: '#e6f4ea', color: '#1e6b35' },
  gesluit: { label: 'Gesluit', background: '#fdecd2', color: '#8a5a13' }
};

const pilStyle = (aktief) => ({
  padding: '8px 14px',
  borderRadius: '20px',
  border: '2px solid #0e1e3b',
  backgroundColor: aktief ? '#0e1e3b' : 'white',
  color: aktief ? 'white' : '#0e1e3b',
  cursor: 'pointer',
  fontWeight: 'bold',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px'
});

const pilStatusStyle = (aktief) => ({
  fontSize: '0.7rem',
  fontWeight: 'normal',
  opacity: aktief ? 0.85 : 0.7
});

// Calls onNuus whenever the server announces new punte (a beoordelaar submitting, or simulated
// punte), so the overview stays live during the event. A burst of submissions becomes one
// reload. EventSource can't send the token, so the stream is read with fetch.
function useLewendeOpdaterings(onNuus) {
  const onNuusRef = useRef(onNuus);
  useEffect(() => { onNuusRef.current = onNuus; });

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const abortController = new AbortController();
    let herkoppel = null;
    let wag = null;
    const meld = () => {
      if (wag) return;
      wag = setTimeout(() => { wag = null; onNuusRef.current(); }, 1000);
    };

    async function luister() {
      try {
        const response = await fetch(`${API_BASE_URL}/stream`, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
          signal: abortController.signal
        });
        if (!response.ok) return; // not allowed (e.g. expired token) - retrying won't help
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop(); // keep a partial line for the next chunk
          if (lines.some(line => line.startsWith('data: '))) meld();
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Stream error:', err);
      }
      // The stream closed (e.g. server restart or proxy timeout) - reconnect after a moment
      if (!abortController.signal.aborted) herkoppel = setTimeout(luister, 5000);
    }

    luister();
    return () => {
      abortController.abort();
      clearTimeout(herkoppel);
      clearTimeout(wag);
    };
  }, []);
}

const thStyle = { padding: '5px', borderBottom: '2px solid #0e1e3b', color: '#0e1e3b', textAlign: 'center', fontSize: '0.82rem' };
const tdStyle = { padding: '4px 6px', borderBottom: '1px solid #e0e0e0', textAlign: 'center', whiteSpace: 'nowrap' };
const pct = (waarde) => (waarde === null || waarde === undefined ? '–' : `${waarde.toFixed(1)}%`);

// The overall standings: every team's mark over all started rounds (every marksheet pooled),
// with its mark per round. `herlaai` reloads it quietly.
function AlgeheleStand({ herlaai }) {
  const [stand, setStand] = useState(null);
  const [fout, setFout] = useState('');

  useEffect(() => {
    let gekanselleer = false;
    getStand()
      .then(data => { if (!gekanselleer) { setStand(data); setFout(''); } })
      .catch(err => { if (!gekanselleer) setFout(err.message); });
    return () => { gekanselleer = true; };
  }, [herlaai]);

  if (!stand) return fout ? <p style={{ color: '#a32020', margin: 0 }}>{fout}</p> : <p style={{ margin: 0 }}>Laai stand...</p>;
  if (stand.rondtes.length === 0) return <p style={{ color: '#666', margin: 0 }}>Nog geen rondte het begin nie.</p>;

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', backgroundColor: 'white' }}>
        <thead>
          <tr>
            <th style={thStyle}>#</th>
            <th style={{ ...thStyle, textAlign: 'left' }}>Span</th>
            <th style={thStyle}>Algeheel</th>
            {stand.rondtes.map(r => <th key={r.rondte_id} style={thStyle}>{r.naam}</th>)}
          </tr>
        </thead>
        <tbody>
          {stand.spanne.map(s => (
            <tr key={s.span_id}>
              <td style={{ ...tdStyle, fontWeight: 'bold' }}>{s.persentasie === null ? '' : s.plek}</td>
              <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 'bold', color: '#0e1e3b' }}>{s.naam}</td>
              <td style={{ ...tdStyle, fontWeight: 'bold', color: '#0e1e3b' }}>{pct(s.persentasie)}</td>
              {stand.rondtes.map(r => {
                const inRondte = s.rondtes[r.rondte_id];
                return (
                  <td key={r.rondte_id} style={{ ...tdStyle, color: '#444' }}>
                    {inRondte ? pct(inRondte.persentasie) : '–'}
                    {inRondte && <span style={{ color: '#999', fontSize: '0.8rem' }}> ({inRondte.beoordelaars})</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ color: '#666', fontSize: '0.85rem', margin: '0.5rem 0 0 0' }}>
        Algeheel: al die punte van al die rondtes saam, gedeel deur die moontlike punte. (n) = beoordelaars in daardie rondte.
      </p>
    </div>
  );
}

// Every nota of the round's teams, grouped by team so the admin sees all comments together.
// `herlaai` reloads them quietly (the list stays on screen while new notas load).
function Kommentaar({ rondte, herlaai }) {
  const [notas, setNotas] = useState(null);
  const [fout, setFout] = useState('');

  useEffect(() => {
    setNotas(null);
  }, [rondte.rondte_id]);

  useEffect(() => {
    let gekanselleer = false;
    setFout('');
    getNotasByRondteId(rondte.rondte_id)
      .then(data => { if (!gekanselleer) setNotas(data); })
      .catch(err => { if (!gekanselleer) setFout(err.message); });
    return () => { gekanselleer = true; };
  }, [rondte.rondte_id, herlaai]);

  if (fout) return <p style={{ color: '#a32020', margin: 0 }}>{fout}</p>;
  if (!notas) return <p style={{ margin: 0 }}>Laai kommentaar...</p>;
  if (notas.length === 0) return <p style={{ color: '#666', margin: 0 }}>Nog geen kommentaar nie.</p>;

  const groepe = [];
  notas.forEach(n => {
    let groep = groepe.find(g => g.span_id === n.span_id);
    if (!groep) {
      groep = { span_id: n.span_id, span_naam: n.span_naam, notas: [] };
      groepe.push(groep);
    }
    groep.notas.push(n);
  });

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      {groepe.map(groep => (
        <div key={groep.span_id} style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '0.75rem 1rem' }}>
          <h4 style={{ margin: '0 0 0.5rem 0', color: '#0e1e3b' }}>{groep.span_naam}</h4>
          {groep.notas.map(n => (
            <p key={n.id} style={{ margin: '0 0 0.4rem 0' }}>
              <strong style={{ color: '#77502d' }}>{n.beoordelaar}:</strong> {n.nota}
            </p>
          ))}
        </div>
      ))}
    </div>
  );
}

function Dashboard() {
  const [plan, setPlan] = useState(null);
  const [fout, setFout] = useState('');
  const [gekoseRondteId, setGekoseRondteId] = useState(null);
  const [toonStand, setToonStand] = useState(false); // the overall standings tab instead of a round
  const [roundPunte, setRoundPunte] = useState([]);
  const [merkblads, setMerkblads] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedSpanId, setSelectedSpanId] = useState(null);
  const [laaiData, setLaaiData] = useState(false);
  const [herlaai, setHerlaai] = useState(0); // bumped when new punte arrive
  const gelaaiVir = useRef(null); // the round whose data is on screen

  // New punte: reload the plan (statuses, counts) and the round's data
  useLewendeOpdaterings(() => setHerlaai(prev => prev + 1));

  const laai = useCallback(async () => {
    try {
      const data = await getPlan();
      setPlan(data);
    } catch (err) {
      setFout(err.message);
    }
  }, []);

  useEffect(() => { laai(); }, [laai, herlaai]);

  const rondtes = plan ? plan.rondtes : [];
  const gestarte = rondtes.filter(r => r.status !== 'beplan');
  const huidige = gestarte[gestarte.length - 1] || null;
  const gekoseRondte = rondtes.find(r => r.rondte_id === gekoseRondteId)
    || huidige
    || rondtes[rondtes.length - 1]
    || null;
  const aktieweId = gekoseRondte ? gekoseRondte.rondte_id : null;

  // Load the punte, merkblad and teams of the chosen round. A live reload of the same round
  // happens quietly: the tables and the selected team stay on screen.
  useEffect(() => {
    if (aktieweId == null) {
      setRoundPunte([]);
      setMerkblads([]);
      setTeams([]);
      gelaaiVir.current = null;
      return;
    }
    let gekanselleer = false;
    if (gelaaiVir.current !== aktieweId) {
      setLaaiData(true);
      setSelectedSpanId(null);
    }
    Promise.all([
      getPunteByRondteId(aktieweId),
      getMerkbladsByRondteId(aktieweId),
      fetchAllTeams()
    ])
      .then(([punte, merkbladsData, teamData]) => {
        if (gekanselleer) return;
        setRoundPunte(punte);
        setMerkblads(merkbladsData);
        setTeams(teamData);
        gelaaiVir.current = aktieweId;
      })
      .catch(err => { if (!gekanselleer) setFout(err.message); })
      .finally(() => { if (!gekanselleer) setLaaiData(false); });
    return () => { gekanselleer = true; };
  }, [aktieweId, herlaai]);

  if (!plan) {
    return <div style={{ padding: '2rem' }}>{fout ? `Fout: ${fout}` : 'Laai oorsig...'}</div>;
  }

  const laaste = rondtes[rondtes.length - 1];
  const wenner = laaste && laaste.uitslag.length > 0 ? laaste.uitslag : null;

  // Only the teams taking part in the round are compared (optional visits to other teams
  // still show in their own breakdown)
  const rondteSpanne = gekoseRondte ? gekoseRondte.spanne : [];
  const comparisonTeams = rondteSpanne.length > 0 ? teams.filter(t => rondteSpanne.includes(t.span_id)) : teams;
  const selectedSpan = teams.find(t => t.span_id === selectedSpanId);
  const selectedSpanPunte = roundPunte.filter(p => p.span_id === selectedSpanId);

  // Download every punt of the round as a CSV (one row per span + beoordelaar, with a column
  // for each kriteria). The file is opened by the spreadsheet program, with UTF-8 BOM.
  const exportCsv = () => {
    const csvEscape = (value) => {
      const s = String(value ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const kriteria = merkblads.map(m => ({
      kriteria_id: m.kriteria_id,
      naam: m.kriteria_beskrywing || `Kriteria ${m.kriteria_id}`,
      totaal: m.totaal
    }));

    const perSpanBeoordelaar = {};
    roundPunte.forEach(punt => {
      const sleutel = `${punt.span_id}|${punt.beoordelaar}`;
      if (!perSpanBeoordelaar[sleutel]) {
        perSpanBeoordelaar[sleutel] = { span_naam: punt.span_naam, beoordelaar: punt.beoordelaar, punte: {} };
      }
      perSpanBeoordelaar[sleutel].punte[punt.kriteria_id] = { punt: punt.punt, totaal: punt.totaal };
    });

    const reeks = [];
    const koppe = ['Span', 'Beoordelaar'];
    kriteria.forEach(k => koppe.push(`${k.naam} (/${k.totaal})`));
    koppe.push('Totaal', 'Persentasie');
    reeks.push(koppe.map(csvEscape).join(','));

    Object.values(perSpanBeoordelaar)
      .sort((a, b) => a.span_naam.localeCompare(b.span_naam) || a.beoordelaar.localeCompare(b.beoordelaar))
      .forEach(r => {
        const ry = [r.span_naam, r.beoordelaar];
        let totaal = 0;
        let moontlik = 0;
        kriteria.forEach(k => {
          const telling = r.punte[k.kriteria_id];
          if (telling) {
            ry.push(telling.punt);
            totaal += telling.punt;
            moontlik += telling.totaal;
          } else {
            ry.push('');
          }
        });
        ry.push(moontlik > 0 ? `${totaal}/${moontlik}` : '');
        ry.push(moontlik > 0 ? `${((totaal / moontlik) * 100).toFixed(1)}%` : '');
        reeks.push(ry.map(csvEscape).join(','));
      });

    const blob = new Blob(['\uFEFF' + reeks.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const aflaai = document.createElement('a');
    aflaai.href = url;
    aflaai.download = `${(gekoseRondte ? gekoseRondte.naam : 'Rondte').replace(/\s+/g, '_')}_punte.csv`;
    document.body.appendChild(aflaai);
    aflaai.click();
    document.body.removeChild(aflaai);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: '1rem 1.25rem', width: '100%', maxWidth: '100%', margin: 0 }}>
      <h2 style={{ color: '#0e1e3b', margin: '0 0 0.4rem 0' }}>Oorsig</h2>
      <p style={{ color: '#666', margin: '0 0 1rem 0' }}>
        Die span-uitslae en alle kommentaar van 'n rondte op een bladsy. {plan.spanne} spanne · {plan.beoordelaars} beoordelaars.
        Klik op enige span om te sien wat elke beoordelaar gegee het.
      </p>

      {fout && (
        <div style={{ padding: '1rem', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px', marginBottom: '1rem' }}>
          Fout: {fout}
        </div>
      )}

      {wenner && (
        <div style={{ backgroundColor: '#0e1e3b', color: 'white', borderRadius: '8px', padding: '14px 20px', marginBottom: '14px' }}>
          <h2 style={{ margin: '0 0 6px 0', color: 'white' }}>🏆 Wenner: {wenner[0].naam}</h2>
          <div>{wenner.slice(1, 3).map(w => `${w.rank}. ${w.naam}`).join(' · ')}</div>
        </div>
      )}

      {rondtes.length === 0 ? (
        <p>Daar is nog geen rondtes nie. Skep 'n plan onder Rondtes.</p>
      ) : (
        <>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '0.9rem' }}>
            <button type="button" onClick={() => setToonStand(true)} style={pilStyle(toonStand)}>
              Algehele stand
            </button>
            {rondtes.map(r => {
              const aktief = !toonStand && gekoseRondte && gekoseRondte.rondte_id === r.rondte_id;
              return (
                <button
                  key={r.rondte_id}
                  type="button"
                  onClick={() => { setGekoseRondteId(r.rondte_id); setToonStand(false); }}
                  style={pilStyle(aktief)}
                >
                  {r.naam}
                  <span style={pilStatusStyle(aktief)}>
                    · {STATUS[r.status] ? STATUS[r.status].label : ''}
                  </span>
                </button>
              );
            })}
          </div>

          {toonStand && (
            <div style={cardStyle}>
              <h3 style={{ marginTop: 0, color: '#0e1e3b' }}>Algehele stand</h3>
              <AlgeheleStand herlaai={herlaai} />
            </div>
          )}

          {!toonStand && gekoseRondte && (
            <>
              <div style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                  <h3 style={{ margin: 0, color: '#0e1e3b' }}>Span-uitslae — {gekoseRondte.naam}</h3>
                  {roundPunte.length > 0 && (
                    <button
                      type="button"
                      onClick={exportCsv}
                      style={{
                        padding: '8px 14px',
                        backgroundColor: '#0e1e3b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      Laai CSV af ({gekoseRondte.naam})
                    </button>
                  )}
                </div>

                {gekoseRondte.status === 'beplan' ? (
                  <p style={{ color: '#666' }}>Hierdie rondte het nog nie begin nie.</p>
                ) : laaiData ? (
                  <p>Laai resultate...</p>
                ) : (
                  <>
                    <SpanVergelyking
                      teams={comparisonTeams}
                      punte={roundPunte}
                      merkblads={merkblads}
                      selectedSpanId={selectedSpanId}
                      onSelectSpan={setSelectedSpanId}
                    />

                    {selectedSpanId && (
                      <SpanScoresBreakdown
                        spanId={selectedSpanId}
                        spanNaam={selectedSpan?.naam}
                        punte={selectedSpanPunte}
                        merkblads={merkblads}
                        refreshKey={herlaai}
                      />
                    )}
                  </>
                )}
              </div>

              <div style={cardStyle}>
                <h3 style={{ marginTop: 0, color: '#0e1e3b' }}>Kommentaar</h3>
                <Kommentaar rondte={gekoseRondte} herlaai={herlaai} />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default Dashboard;
